import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Canonical surface URLs (Option A — Presence stays local, but cross-links to
// People Engine and Control Room point at their canonical external apps).
// Defaults fall back to in-repo routes if the env var isn't supplied so local
// dev / preview keeps working.
const _normalize = (u, fallback) =>
  (u || fallback || "").replace(/\/+$/, "");
// Environment isolation (hard rule): production Presence must never talk to a
// preview People Engine. The production host mapping wins over the env value.
const _PROD_PE_BY_HOST = { "presence.1os.in": "https://people.1os.in" };
const _hostPinnedPe =
  typeof window !== "undefined"
    ? _PROD_PE_BY_HOST[window.location.hostname]
    : undefined;
export const PEOPLE_ENGINE_URL = _normalize(
  _hostPinnedPe || process.env.REACT_APP_PEOPLE_ENGINE_URL, "",
);
export const CONTROL_ROOM_URL = _normalize(
  process.env.REACT_APP_CONTROL_ROOM_URL, "",
);
export const peopleEngineLink = (path = "/login") =>
  PEOPLE_ENGINE_URL ? `${PEOPLE_ENGINE_URL}${path}` : `/people${path}`;
export const controlRoomLink = (path = "/login") =>
  CONTROL_ROOM_URL ? `${CONTROL_ROOM_URL}${path}` : `/control${path}`;

// Manager (admin) token storage
const MGR_KEY = "1os_mgr_token";
const MGR_OBJ = "1os_mgr";
const DEV_KEY = "device_token";
const DEV_OUTLET = "device_outlet";

// People's Engine — fresh, short-lived in-memory + sessionStorage so the
// session doesn't survive an accidental tab close. Control Room access can
// still use the longer-lived MGR_KEY (governance/read-only).
const PE_KEY = "1os_people_engine_token";
const PE_MGR = "1os_people_engine_mgr";
const PE_CAPS = "1os_people_engine_caps";

export function setPeopleEngineSession(token, manager, capabilities) {
  // sessionStorage wipes on tab close — manager must re-authenticate.
  sessionStorage.setItem(PE_KEY, token);
  sessionStorage.setItem(PE_MGR, JSON.stringify(manager || {}));
  sessionStorage.setItem(PE_CAPS, JSON.stringify(capabilities || []));
}
export function getPeopleEngineToken() {
  return sessionStorage.getItem(PE_KEY);
}
export function getPeopleEngineManager() {
  try { return JSON.parse(sessionStorage.getItem(PE_MGR) || "null"); } catch { return null; }
}
export function getPeopleEngineCapabilities() {
  try { return JSON.parse(sessionStorage.getItem(PE_CAPS) || "[]"); } catch { return []; }
}
export function hasCapability(cap) {
  return getPeopleEngineCapabilities().includes(cap);
}
export function clearPeopleEngineSession() {
  sessionStorage.removeItem(PE_KEY);
  sessionStorage.removeItem(PE_MGR);
  sessionStorage.removeItem(PE_CAPS);
}

export function setManagerSession(token, manager) {
  localStorage.setItem(MGR_KEY, token);
  localStorage.setItem(MGR_OBJ, JSON.stringify(manager));
}
export function getManagerToken() {
  return localStorage.getItem(MGR_KEY);
}
export function getManager() {
  try { return JSON.parse(localStorage.getItem(MGR_OBJ) || "null"); } catch { return null; }
}
export function clearManagerSession() {
  localStorage.removeItem(MGR_KEY);
  localStorage.removeItem(MGR_OBJ);
}

export function setDeviceSession(token, outlet, device) {
  localStorage.setItem(DEV_KEY, token);
  localStorage.setItem(DEV_OUTLET, JSON.stringify({ outlet, device }));
}
export function getDeviceToken() {
  return localStorage.getItem(DEV_KEY);
}
export function getDeviceMeta() {
  try { return JSON.parse(localStorage.getItem(DEV_OUTLET) || "null"); } catch { return null; }
}
export function clearDeviceSession() {
  localStorage.removeItem(DEV_KEY);
  localStorage.removeItem(DEV_OUTLET);
}

// PWA device token (short-lived, issued by /presence/pwa/enroll).
const PWA_KEY = "pwa_device_token";
const PWA_META = "pwa_device_meta";

export function setPwaSession(token, meta) {
  localStorage.setItem(PWA_KEY, token);
  if (meta) localStorage.setItem(PWA_META, JSON.stringify(meta));
}
export function getPwaToken() {
  return localStorage.getItem(PWA_KEY);
}
export function getPwaMeta() {
  try { return JSON.parse(localStorage.getItem(PWA_META) || "null"); } catch { return null; }
}
export function clearPwaSession() {
  localStorage.removeItem(PWA_KEY);
  localStorage.removeItem(PWA_META);
}

// Axios instance for manager-authenticated calls
export const api = axios.create({ baseURL: API_BASE });
api.interceptors.request.use((cfg) => {
  const t = getManagerToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Device-authenticated axios
export const deviceApi = axios.create({ baseURL: API_BASE });
deviceApi.interceptors.request.use((cfg) => {
  const t = getDeviceToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// People's Engine axios — uses a fresh sessionStorage token, never falls back
// to the long-lived Control Room manager token.
export const peopleApi = axios.create({ baseURL: API_BASE });
peopleApi.interceptors.request.use((cfg) => {
  const t = getPeopleEngineToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export function formatDetail(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (d?.msg ? d.msg : JSON.stringify(d))).join(" ");
  }
  if (detail?.msg) return detail.msg;
  return String(detail);
}
